import React, { useState, useEffect } from "react";
import { __ } from "services/Translator";
import { Dropdown } from "react-bootstrap";
import { SELECTABLE_COUNTRIES, countryFlagPath } from "configs/countrySelector";
import api from "services/api";
import {
  getRequestCountry,
  getManualCountry,
  setManualCountry,
  resolveGeoCountry,
  COUNTRY_CHANGED_EVENT,
  GEO_RESOLVED_EVENT,
} from "services/requestCountry";

// English display name for any ISO2 code ("NL" -> "Netherlands"). Falls back to
// the raw code if Intl.DisplayNames is unavailable.
const regionDisplay = (() => {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" });
  } catch (_) {
    return null;
  }
})();
function countryName(code) {
  const c = (code || "").toUpperCase();
  if (!c) return "";
  try {
    return (regionDisplay && regionDisplay.of(c)) || c;
  } catch (_) {
    return c;
  }
}

// Visible region/country picker (P2b-2). Styled to match the language switcher
// (reuses its `languageSwitcherDropdown` classes) and placed next to it. Lets a
// visitor choose which country's content variant to see; the choice is a manual
// session override (services/requestCountry) that outranks account/geo. Picking
// reloads so cached content is refetched with the new `X-Request-Country`.
//
// Renders nothing when SELECTABLE_COUNTRIES is empty (non-GCC brands) — the
// whole feature stays inert.

const chevronSvg = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M4.293 8.293a1 1 0 0 1 1.414 0L12 14.586l6.293-6.293a1 1 0 1 1 1.414 1.414l-7 7a1 1 0 0 1-1.414 0l-7-7a1 1 0 0 1 0-1.414"
      fill="currentColor"
    />
  </svg>
);

// Fallback icon when the current country is unknown (no flag to show).
const globeSvg = (
  <svg className="flag-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
    <path d="M3 12h18M12 3c2.6 2.7 2.6 15.3 0 18M12 3c-2.6 2.7-2.6 15.3 0 18" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

export default function CountrySwitcherDropdown(props) {
  const [menuOpen, setMenuOpen] = useState(false);
  // Re-render in place when the resolved country changes (manual pick, geo
  // resolves at startup, or account country on login) — no page reload.
  const [, forceTick] = useState(0);
  useEffect(() => {
    const onChange = () => forceTick((n) => n + 1);
    // COUNTRY_CHANGED_EVENT: manual pick / account change. GEO_RESOLVED_EVENT:
    // the cold-load geo lookup landed (fired by whichever component resolved it),
    // so the flag updates even when another component won the resolve race — the
    // earlier "wrong/absent flag on hard refresh" bug.
    window.addEventListener(COUNTRY_CHANGED_EVENT, onChange);
    window.addEventListener(GEO_RESOLVED_EVENT, onChange);
    // Also resolve geo ourselves so the flag works on pages with no other
    // country-aware component. Idempotent + does not refetch page content.
    resolveGeoCountry(api).then(() => forceTick((n) => n + 1));
    return () => {
      window.removeEventListener(COUNTRY_CHANGED_EVENT, onChange);
      window.removeEventListener(GEO_RESOLVED_EVENT, onChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inSidebar = props.sidebarVariant === true;
  const sidebarFoldedIn = inSidebar && props.isSidebarCollapsed === true;
  const useMobileInlinePanel =
    inSidebar && props.isMobileDevice === true && !sidebarFoldedIn;
  const dropDirection = inSidebar ? (sidebarFoldedIn ? "end" : "up") : undefined;

  // Effective country (manual -> account -> geo). "" when unknown.
  const currentCode = (getRequestCountry() || "").toUpperCase();

  useEffect(() => {
    if (sidebarFoldedIn && menuOpen) setMenuOpen(false);
  }, [sidebarFoldedIn, menuOpen]);

  // Nothing selectable -> render nothing (inert on non-GCC brands).
  if (!SELECTABLE_COUNTRIES.length) return null;

  const closeMenu = () => setMenuOpen(false);

  const pickCountry = (code) => {
    if (inSidebar) closeMenu();
    // No-op if the manual override already resolves to the same country.
    if ((code || "").toUpperCase() === currentCode) return;
    // setManualCountry dispatches COUNTRY_CHANGED_EVENT; country-aware content
    // refetches in place — no full page reload.
    setManualCountry(code);
  };

  // Clear the manual override and fall back to the detected (geo) country.
  const pickAutomatic = () => {
    if (inSidebar) closeMenu();
    if (!getManualCountry()) return;
    setManualCountry("");
  };

  // Show the visitor's real resolved country (flag + English name), target
  // market or not — GCC accepts allowed countries, so an NL visitor sees the NL
  // flag + "Netherlands" and gets the default content. Globe only when unknown.
  const toggleLabelText = currentCode ? countryName(currentCode) : __("Region");
  const toggleShort = currentCode || "—";
  const toggleFlag = currentCode ? countryFlagPath(currentCode) : "";
  const flagOrGlobe = toggleFlag ? (
    <img src={toggleFlag} alt="" className="flag-icon" />
  ) : (
    globeSvg
  );

  // Header usage: a plain, non-interactive flag indicator (no dropdown).
  if (props.indicatorOnly) {
    return (
      <span
        className="countryFlagIndicator"
        title={toggleLabelText}
        aria-label={toggleLabelText}
      >
        {flagOrGlobe}
      </span>
    );
  }

  const renderToggleLabel = () => (
    <>
      <div className="menu-item-area">
        <div className="menu-icon">{flagOrGlobe}</div>
        <div className="menu-text language-menu-fullname">{toggleLabelText}</div>
        {!sidebarFoldedIn ? (
          <div className="menu-text language-menu-short">{toggleShort}</div>
        ) : null}
      </div>
      {!sidebarFoldedIn ? <div className="accordion-chevron">{chevronSvg}</div> : null}
    </>
  );

  const renderMenuHeader = () => (
    <div className="languageSwitcherDropdown__menuHeader">
      <button
        type="button"
        className="languageSwitcherDropdown__menuClose"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          closeMenu();
        }}
        aria-label={__("Close")}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </div>
  );

  const renderRow = (country, asButton) => {
    const isActive = country.code === currentCode;
    const rowClass = [
      isActive ? "selected active" : null,
      inSidebar ? "languageSwitcherDropdown__menuRow" : null,
    ]
      .filter(Boolean)
      .join(" ");

    const rowBody = (
      <div className="language-item-content language-item-content--play365">
        <span className="language-flag-circle">
          <img src={countryFlagPath(country.code)} alt="" />
        </span>
        <span className="language-name-full">{country.name}</span>
        <span className="language-code">{country.code}</span>
      </div>
    );

    if (asButton) {
      return (
        <button key={country.code} type="button" className={rowClass} onClick={() => pickCountry(country.code)}>
          {rowBody}
        </button>
      );
    }
    return (
      <Dropdown.Item key={country.code} onClick={() => pickCountry(country.code)} className={rowClass}>
        {rowBody}
      </Dropdown.Item>
    );
  };

  const renderHeaderRow = (country) => (
    <Dropdown.Item
      key={country.code}
      onClick={() => pickCountry(country.code)}
      className={country.code === currentCode ? "selected active" : null}
    >
      <div className="language-item-content">
        <img src={countryFlagPath(country.code)} alt="" className="flag-icon" />
        <label htmlFor={"country-" + country.code} className="text fullName">
          {country.name}
        </label>
      </div>
      <input
        type="radio"
        id={"country-" + country.code}
        checked={country.code === currentCode}
        name="country"
        readOnly
      />
    </Dropdown.Item>
  );

  const renderList = (asButtons) =>
    SELECTABLE_COUNTRIES.map((country) =>
      inSidebar ? renderRow(country, asButtons) : renderHeaderRow(country)
    );

  // Header menu: "Automatic (detected)" on top — active when no manual override,
  // and picking it clears a stuck override back to the geo-detected country — then
  // the selectable target markets below (picking one sets a manual override).
  const manualSet = !!getManualCountry();
  const renderHeaderMenu = () => (
    <>
      <Dropdown.Item
        onClick={pickAutomatic}
        className={!manualSet ? "selected active" : undefined}
      >
        <div className="language-item-content">
          {toggleFlag ? (
            <img src={toggleFlag} alt="" className="flag-icon" />
          ) : (
            <span className="flag-icon" aria-hidden="true" />
          )}
          <label className="text fullName">
            {__("Automatic")}
            {currentCode ? ` — ${countryName(currentCode)}` : ""}
          </label>
        </div>
      </Dropdown.Item>
      {SELECTABLE_COUNTRIES.map((country) => (
        <Dropdown.Item
          key={country.code}
          onClick={() => pickCountry(country.code)}
          className={
            manualSet && country.code === currentCode ? "selected active" : undefined
          }
        >
          <div className="language-item-content">
            <img
              src={countryFlagPath(country.code)}
              alt=""
              className="flag-icon"
            />
            <label className="text fullName">{country.name}</label>
          </div>
        </Dropdown.Item>
      ))}
    </>
  );

  const renderAutomaticRow = (asButton) => {
    const rowClass = [
      !manualSet ? "selected active" : null,
      inSidebar ? "languageSwitcherDropdown__menuRow" : null,
    ]
      .filter(Boolean)
      .join(" ");
    const rowBody = (
      <div className="language-item-content language-item-content--play365">
        <span className="language-flag-circle">
          {toggleFlag ? <img src={toggleFlag} alt="" /> : globeSvg}
        </span>
        <span className="language-name-full">
          {__("Automatic")}
          {currentCode ? ` — ${countryName(currentCode)}` : ""}
        </span>
      </div>
    );
    return asButton ? (
      <button type="button" className={rowClass} onClick={pickAutomatic}>
        {rowBody}
      </button>
    ) : (
      <Dropdown.Item onClick={pickAutomatic} className={rowClass}>
        {rowBody}
      </Dropdown.Item>
    );
  };

  const renderSidebarMenuBody = (asButtons) => (
    <>
      {inSidebar && renderMenuHeader()}
      {renderAutomaticRow(asButtons)}
      {renderList(asButtons)}
    </>
  );

  const rootClass = [
    "languageSwitcherDropdown",
    "countrySwitcherDropdown",
    inSidebar ? "languageSwitcherDropdown--sidebar" : "",
    inSidebar && props.isSidebarCollapsed ? "languageSwitcherDropdown--collapsed" : "",
    inSidebar && !props.isSidebarCollapsed ? "languageSwitcherDropdown--expanded" : "",
    useMobileInlinePanel ? "languageSwitcherDropdown--mobileInline" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const menuClassName = inSidebar
    ? "languageSwitcherDropdown__menu languageSwitcherDropdown__menu--play365"
    : "";

  return (
    <div className={rootClass}>
      {useMobileInlinePanel ? (
        <>
          <button
            type="button"
            className={`sidebar-accordion-button languageSwitcherDropdown__toggle${menuOpen ? " show" : ""}`}
            id="dropdown-country"
            aria-label={__("Region")}
            aria-expanded={menuOpen}
            aria-controls="country-switcher-inline-panel"
            onClick={() => setMenuOpen((open) => !open)}
          >
            {renderToggleLabel()}
          </button>
          {menuOpen ? (
            <div
              id="country-switcher-inline-panel"
              className={`${menuClassName} languageSwitcherDropdown__inlinePanel`}
              role="listbox"
              aria-label={__("Region")}
            >
              {renderSidebarMenuBody(true)}
            </div>
          ) : null}
        </>
      ) : (
        <Dropdown show={menuOpen} onToggle={setMenuOpen} drop={dropDirection}>
          {inSidebar ? (
            <Dropdown.Toggle
              variant="link"
              className="sidebar-accordion-button languageSwitcherDropdown__toggle"
              id="dropdown-country"
              aria-label={__("Region")}
            >
              {renderToggleLabel()}
            </Dropdown.Toggle>
          ) : (
            <Dropdown.Toggle
              variant="language"
              id="dropdown-country"
              className="countrySwitcherHeaderToggle"
            >
              {flagOrGlobe}
              <span className="countrySwitcherHeaderName">{toggleLabelText}</span>
              <i
                className="fas fa-caret-down countrySwitcherHeaderCaret"
                aria-hidden="true"
              />
            </Dropdown.Toggle>
          )}

          <Dropdown.Menu align="start" className={menuClassName || undefined}>
            {inSidebar ? renderSidebarMenuBody(false) : renderHeaderMenu()}
          </Dropdown.Menu>
        </Dropdown>
      )}
    </div>
  );
}
